import { LANES } from '@accra/shared';
import { canvasTexture, repeating } from '../../render/materials/canvasTexture';

/**
 * Asphalt with aggregate grain, patched repairs, oil stains down the middle of each lane and worn paint.
 * One texture repeat = 20 m of road.
 */
export function roadTexture() {
  return canvasTexture(512, 1024, (ctx) => {
    ctx.fillStyle = '#3a3a3c';
    ctx.fillRect(0, 0, 512, 1024);
    // Aggregate: lots of tiny light and dark specks.
    for (let i = 0; i < 26000; i += 1) {
      const light = Math.random() > 0.5;
      ctx.fillStyle = light ? `rgba(200,200,195,${Math.random() * 0.12})` : `rgba(0,0,0,${Math.random() * 0.18})`;
      ctx.fillRect(Math.random() * 512, Math.random() * 1024, 1 + Math.random() * 2, 1 + Math.random() * 2);
    }
    // Patched repairs: slightly darker, squarer patches.
    for (let i = 0; i < 5; i += 1) {
      ctx.fillStyle = `rgba(20,20,22,${0.25 + Math.random() * 0.2})`;
      ctx.fillRect(Math.random() * 420, Math.random() * 960, 40 + Math.random() * 90, 30 + Math.random() * 120);
    }
    // Oil and tyre wear down the middle of each lane.
    for (let lane = 0; lane < LANES; lane += 1) {
      const centre = (512 / LANES) * (lane + 0.5);
      const wear = ctx.createLinearGradient(centre - 60, 0, centre + 60, 0);
      wear.addColorStop(0, 'rgba(0,0,0,0)');
      wear.addColorStop(0.5, 'rgba(10,8,6,0.22)');
      wear.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = wear;
      ctx.fillRect(centre - 60, 0, 120, 1024);
    }
    // Worn white paint: edge lines and dashed lane lines, a little broken up.
    const paint = (x: number, y: number, w: number, h: number): void => {
      ctx.fillStyle = 'rgba(233,228,214,0.82)';
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = 'rgba(58,58,60,0.45)';
      for (let i = 0; i < h / 6; i += 1) ctx.fillRect(x + Math.random() * w, y + Math.random() * h, 3, 2);
    };
    paint(8, 0, 9, 1024);
    paint(495, 0, 9, 1024);
    for (let lane = 1; lane < LANES; lane += 1) {
      const x = (512 / LANES) * lane - 4;
      for (let y = 0; y < 1024; y += 256) paint(x, y, 9, 140);
    }
  });
}

/** Black-and-white painted kerb, very common on Accra roads. */
export function kerbTexture() {
  return repeating(
    canvasTexture(64, 16, (ctx) => {
      ctx.fillStyle = '#f2f2f2';
      ctx.fillRect(0, 0, 64, 16);
      ctx.fillStyle = '#1b1b1b';
      ctx.fillRect(0, 0, 32, 16);
    }),
    20,
    1,
  );
}

/** Yellow-and-black median barrier. */
export function medianTexture() {
  return repeating(
    canvasTexture(64, 16, (ctx) => {
      ctx.fillStyle = '#f2c14e';
      ctx.fillRect(0, 0, 64, 16);
      ctx.fillStyle = '#1b1b1b';
      ctx.fillRect(32, 0, 32, 16);
    }),
    20,
    1,
  );
}

export function signTexture(text: string, bg: string, fg: string) {
  return canvasTexture(512, 96, (ctx) => {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 512, 96);
    ctx.strokeStyle = fg;
    ctx.lineWidth = 6;
    ctx.strokeRect(6, 6, 500, 84);
    ctx.fillStyle = fg;
    ctx.font = 'bold 44px Impact, "Arial Black", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 50, 480);
  });
}
